import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// DEBUG: Disable in production
const DEBUG_MODE = false;
const debugLog = (...args) => {
  if (DEBUG_MODE) {
    console.log('[AuthContext]', new Date().toISOString(), ...args);
  }
};

// Configure axios interceptor to always add token from localStorage
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(
    JSON.parse(localStorage.getItem('currentOrganization') || 'null')
  );

  useEffect(() => {
    debugLog('Token changed, fetching user:', token ? 'present' : 'null');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      if (response.data.current_organization) {
        setCurrentOrganization(response.data.current_organization);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      // On kiosk pages, do NOT call logout() — it clears localStorage and fires
      // a storage event that resets CompanyContext in the admin panel tab.
      // Kiosk pages don't need an admin session; just clear local React state.
      if (window.location.pathname.startsWith('/kiosk')) {
        setToken(null);
        setUser(null);
        setCurrentOrganization(null);
      } else {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { access_token, user: userData, organizations: userOrgs } = response.data;
      
      // Store organizations for potential selection
      setOrganizations(userOrgs || []);
      
      // If user has only one organization, log them in directly
      if (userOrgs && userOrgs.length === 1) {
        localStorage.setItem('token', access_token);
        localStorage.setItem('currentOrganization', JSON.stringify(userOrgs[0]));
        setToken(access_token);
        setUser(userData);
        setCurrentOrganization(userOrgs[0]);
        return { success: true };
      }
      
      // If user has multiple organizations, return for org selection
      if (userOrgs && userOrgs.length > 1) {
        return { 
          success: true, 
          requiresOrgSelection: true,
          tempToken: access_token,
          user: userData,
          organizations: userOrgs
        };
      }
      
      // If user has no organizations (superadmin without orgs or legacy user)
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Error al iniciar sesión' 
      };
    }
  };

  const selectOrganization = async (organizationId, tempToken) => {
    debugLog('selectOrganization START:', organizationId);
    try {
      // Make request with temp token explicitly
      const response = await axios.post(`${API_URL}/auth/select-organization`, 
        { organization_id: organizationId },
        { headers: { 'Authorization': `Bearer ${tempToken}` } }
      );
      
      const { access_token, organization } = response.data;
      
      debugLog('selectOrganization RESPONSE:', {
        access_token: access_token ? 'present' : 'null',
        organization: organization?.name
      });
      
      if (!access_token) {
        debugLog('ERROR: No access_token in response');
        return { success: false, error: 'No access token received' };
      }
      
      // Update localStorage first - this is critical for state consistency
      debugLog('Updating localStorage...');
      localStorage.setItem('token', access_token);
      localStorage.setItem('currentOrganization', JSON.stringify(organization));
      
      // Update state
      debugLog('Updating React state...');
      setToken(access_token);
      setCurrentOrganization(organization);
      
      // Fetch full user data - axios interceptor will use new token from localStorage
      debugLog('Fetching user data...');
      await fetchUser();
      
      // Notify other contexts that organization changed
      debugLog('Dispatching storage event for organization change...');
      window.dispatchEvent(new StorageEvent('storage', { 
        key: 'currentOrganization', 
        newValue: JSON.stringify(organization) 
      }));
      
      debugLog('selectOrganization COMPLETE');
      return { success: true };
    } catch (error) {
      debugLog('selectOrganization ERROR:', error.message);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Error al seleccionar organización' 
      };
    }
  };

  const continueWithoutOrg = (tempToken, tempUser) => {
    // For superadmins who want to access admin panel without selecting an org
    localStorage.setItem('token', tempToken);
    localStorage.setItem('user', JSON.stringify(tempUser));
    setToken(tempToken);
    setUser(tempUser);
    setCurrentOrganization(null);
    return { success: true };
  };

  const verifyEmail = async (verificationToken) => {
    try {
      const response = await axios.post(`${API_URL}/signup/verify-email`, { token: verificationToken });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Error al confirmar el email'
      };
    }
  };

  const logout = () => {
    debugLog('logout called - clearing all state');

    // Best-effort server-side revocation so the token can't be reused even
    // though it hasn't expired yet. Fire-and-forget: don't block local logout.
    const currentToken = token || localStorage.getItem('token');
    if (currentToken) {
      axios.post(`${API_URL}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${currentToken}` }
      }).catch(() => {});
    }

    // Clear localStorage first
    localStorage.removeItem('token');
    localStorage.removeItem('currentOrganization');
    localStorage.removeItem('user');
    
    // Clear state
    setToken(null);
    setUser(null);
    setCurrentOrganization(null);
    setOrganizations([]);
    
    // Dispatch storage event manually to notify other contexts (CompanyContext)
    // since storage events don't fire in the same window
    debugLog('Dispatching storage event for logout');
    window.dispatchEvent(new StorageEvent('storage', { key: 'token', newValue: null }));
  };

  const isSuperadmin = () => user?.role === 'superadmin';
  const isAdmin = () => user?.role === 'admin' || isSuperadmin();
  const isSupervisor = () => user?.role === 'supervisor';
  const isWorker = () => user?.role === 'worker';
  const isInspector = () => user?.role === 'inspector';
  const isGestor = () => user?.role === 'gestor';
  const canManage = () => isAdmin() || isSupervisor();

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      organizations,
      currentOrganization,
      login,
      selectOrganization,
      continueWithoutOrg,
      verifyEmail,
      logout,
      isSuperadmin,
      isAdmin,
      isSupervisor,
      isWorker,
      isInspector,
      isGestor,
      canManage
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
